import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { useWordPress } from '../hooks/useWordPress';
import './DiveSitesPage.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const DiveSitesPage = () => {
  const [diveSites, setDiveSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    country: '',
    max_depth: '',
    visibility_avg: '',
    water_temp_avg: '',
    difficulty_level: 'intermediate',
    description: '',
    marine_life: '',
    best_time_to_visit: '',
    access_type: 'boat'
  });

  const API_BASE = `${process.env.REACT_APP_WORDPRESS_URL || ''}/wp-json/nbs-dive-db/v1`;

  const { usePostsForMap } = useWordPress();
  const { data: postsData } = usePostsForMap();

  // Build lookup: { [dive_site_id]: [{ title, slug }, ...] }
  // A post may list multiple dive site IDs; a site may be referenced by multiple posts.
  const postsBySiteId = (postsData || []).reduce((acc, post) => {
    const siteIds = post.meta?.dive_site_id; // array because single:false in PHP
    if (Array.isArray(siteIds)) {
      siteIds.forEach(id => {
        if (id > 0) {
          if (!acc[id]) acc[id] = [];
          acc[id].push({
            title: post.title?.rendered || post.title,
            slug: post.slug,
          });
        }
      });
    }
    return acc;
  }, {});

  useEffect(() => {
    loadDiveSites();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterDiveSites();
  }, [diveSites, searchTerm, activeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDiveSites = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/dive-sites`);
      const data = await response.json();

      if (response.ok) {
        setDiveSites(data);
        setError(null);
      } else {
        setError('Failed to load dive sites');
      }
    } catch (error) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const filterDiveSites = () => {
    let filtered = diveSites;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(site =>
        site.name?.toLowerCase().includes(term) ||
        site.location?.toLowerCase().includes(term) ||
        site.country?.toLowerCase().includes(term) ||
        site.description?.toLowerCase().includes(term) ||
        site.marine_life?.toLowerCase().includes(term)
      );
    }

    // Difficulty filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(site => site.difficulty_level === activeFilter);
    }

    setFilteredSites(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleAddSite = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE}/dive-sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          max_depth: formData.max_depth ? parseInt(formData.max_depth) : null,
          visibility_avg: formData.visibility_avg ? parseInt(formData.visibility_avg) : null,
          water_temp_avg: formData.water_temp_avg ? parseFloat(formData.water_temp_avg) : null,
        })
      });

      if (response.ok) {
        setFormData({
          name: '',
          location: '',
          country: '',
          max_depth: '',
          visibility_avg: '',
          water_temp_avg: '',
          difficulty_level: 'intermediate',
          description: '',
          marine_life: '',
          best_time_to_visit: '',
          access_type: 'boat'
        });
        setShowAddModal(false);
        loadDiveSites();
      } else {
        const errorData = await response.json();
        alert('Failed to add dive site: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to add dive site: ' + error.message);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Filter sites that have valid coordinates for the map
  const sitesWithCoordinates = filteredSites.filter(site =>
    site.latitude && site.longitude &&
    !isNaN(parseFloat(site.latitude)) && !isNaN(parseFloat(site.longitude))
  );

  // Calculate map center based on sites with coordinates
  const getMapCenter = () => {
    if (sitesWithCoordinates.length === 0) {
      return [20, 0]; // Default center with slight northern tilt
    }

    const lats = sitesWithCoordinates.map(site => parseFloat(site.latitude));
    const lngs = sitesWithCoordinates.map(site => parseFloat(site.longitude));

    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

    return [centerLat, centerLng];
  };

  if (loading) {
    return (
      <div className="dive-sites-page">
        <div className="container">
          <div className="loading">Loading dive sites...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dive-sites-page">
        <div className="container">
          <div className="error">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dive-sites-page">
      <div className="container">
        {/* Header */}
        <div className="dive-sites-header">
          <div>
            <h1>Dive Sites</h1>
            <p className="dive-sites-subtitle">A map of the dive sites I've explored so far.</p>
          </div>
          {/* <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add New Site
          </button> */}
        </div>

        {/* Search and Filter */}
        <div className="search-filter-section">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search dive sites or locations..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="filter-buttons">
            {['all', 'beginner', 'intermediate', 'advanced', 'expert'].map(filter => (
              <button
                key={filter}
                className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                onClick={() => handleFilterClick(filter)}
              >
                {filter === 'all' ? 'All Levels' : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Map */}
        <div className="map-wrapper">
          <div className="map-hint">📍 Click a pin to see site details</div>
          {sitesWithCoordinates.length === 0 ? (
            <div className="no-map-data">
              <p>No dive sites with coordinates found to display on the map.</p>
              <p>Add dive sites with latitude and longitude data to see them on the map.</p>
            </div>
          ) : (
            <MapContainer
              center={getMapCenter()}
              zoom={3}
              style={{ height: '100%', width: '100%' }}
              className="dive-sites-map"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {sitesWithCoordinates.map(site => {
                const linkedPosts = postsBySiteId[site.id] || [];
                return (
                  <Marker
                    key={site.id}
                    position={[parseFloat(site.latitude), parseFloat(site.longitude)]}
                  >
                    <Popup>
                      <div className="map-popup">
                        <h3>{site.name}</h3>
                        <p>
                          <strong>Location:</strong>{' '}
                          {site.location}{site.country ? ', ' + site.country : ''}
                        </p>
                        {site.difficulty_level && (
                          <p><strong>Difficulty:</strong> {site.difficulty_level}</p>
                        )}
                        {linkedPosts.length > 0 && (
                          <div className="map-popup-blog-links">
                            {linkedPosts.map(p => (
                              <p key={p.slug} className="map-popup-blog-link">
                                <Link to={`/blog/${p.slug}`}>Read: {p.title}</Link>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </div>

        {/* Add Site Modal */}
        {showAddModal && (
          <div className="modal" onClick={() => setShowAddModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowAddModal(false)}><FaTimes /></button>
              <h2>Add New Dive Site</h2>
              <form onSubmit={handleAddSite}>
                <div className="form-group">
                  <label htmlFor="name">Site Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="location">Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="max_depth">Max Depth (m)</label>
                    <input
                      type="number"
                      id="max_depth"
                      name="max_depth"
                      value={formData.max_depth}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="visibility_avg">Avg Visibility (m)</label>
                    <input
                      type="number"
                      id="visibility_avg"
                      name="visibility_avg"
                      value={formData.visibility_avg}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="water_temp_avg">Avg Water Temp (°C)</label>
                    <input
                      type="number"
                      step="0.1"
                      id="water_temp_avg"
                      name="water_temp_avg"
                      value={formData.water_temp_avg}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="difficulty_level">Difficulty Level</label>
                    <select
                      id="difficulty_level"
                      name="difficulty_level"
                      value={formData.difficulty_level}
                      onChange={handleFormChange}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="access_type">Access Type</label>
                    <select
                      id="access_type"
                      name="access_type"
                      value={formData.access_type}
                      onChange={handleFormChange}
                    >
                      <option value="boat">Boat</option>
                      <option value="shore">Shore</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="marine_life">Marine Life</label>
                  <input
                    type="text"
                    id="marine_life"
                    name="marine_life"
                    value={formData.marine_life}
                    onChange={handleFormChange}
                    placeholder="e.g., Coral reefs, tropical fish, turtles"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="best_time_to_visit">Best Time to Visit</label>
                  <input
                    type="text"
                    id="best_time_to_visit"
                    name="best_time_to_visit"
                    value={formData.best_time_to_visit}
                    onChange={handleFormChange}
                    placeholder="e.g., November to April"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows="4"
                    placeholder="Describe the dive site, what makes it special, etc."
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Add Dive Site</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiveSitesPage;
